import { DOCUMENT } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, EMPTY, map, of, shareReplay, switchMap, timer } from "rxjs";
import { SupportServiceProxy } from "@api/core";
import { AppSessionService } from "@vadmin/account/auth";
import { SupportChatProvider } from "./support-chat-provider";

declare global {
    interface Window {
        hsConversationsSettings?: Record<string, unknown>;
        hsConversationsOnReady?: (() => void)[];
        HubSpotConversations?: { widget: { load(): void; remove(): void; open(): void } };
    }
}

// The first HubSpot integration (PR 1505) waited after remove() before load() and never recorded why; the wait stays.
const LOAD_DELAY_MS = 1000;

@Injectable({
    providedIn: "root",
})
export class HubSpotChatProvider implements SupportChatProvider {
    private _document = inject(DOCUMENT);
    private _session = inject(AppSessionService);
    private _supportApi = inject(SupportServiceProxy);
    private _ready$ = new BehaviorSubject(false);
    private _visible$ = new BehaviorSubject(false);

    // One identification token per signed-in user, however often the widget reloads (VS-1926).
    private _settings$ = combineLatest([this._session.onSessionChanged, this._session.onTenantInfoChanged]).pipe(
        map(([user, tenant]) => (user && tenant ? user : null)),
        distinctUntilChanged((a, b) => a?.id === b?.id),
        switchMap((user) =>
            user
                ? this._supportApi.getIdentifier().pipe(
                      map((token): Record<string, unknown> => ({ identificationEmail: user.emailAddress, identificationToken: token })),
                      catchError(() => of({}))
                  )
                : of({})
        ),
        shareReplay(1)
    );

    load() {
        window.hsConversationsSettings = { loadImmediately: false };
        window.hsConversationsOnReady = [() => this._ready$.next(true)];

        const script = this._document.createElement("script");
        script.id = "hs-script-loader";
        script.src = "//js.hs-scripts.com/39655218.js";
        script.async = true;
        script.defer = true;
        this._document.head.appendChild(script);

        combineLatest([this._ready$, this._visible$.pipe(distinctUntilChanged())])
            .pipe(
                switchMap(([ready, visible]) => (ready && visible ? this._settings$ : of(null))),
                switchMap((settings) => {
                    window.HubSpotConversations?.widget.remove();
                    return settings ? timer(LOAD_DELAY_MS).pipe(map(() => settings)) : EMPTY;
                })
            )
            .subscribe((settings) => {
                window.hsConversationsSettings = settings;
                window.HubSpotConversations?.widget.load();
            });
    }

    setVisible(visible: boolean) {
        this._visible$.next(visible);
    }

    open() {
        window.HubSpotConversations?.widget.open();
    }
}
